import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiShuffle, FiCheck, FiGrid, FiSliders } from 'react-icons/fi';
import { Spinner, Tab, Nav } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { getBrandListing } from '../../../api/context/brand';
import {

    getExternalSubcategoriesByVendor,
    getBrandCategoriesHierarchy,
    saveCategoryMappings,
    getCategoryMappings,
    getSizeMappings,
    saveSizeMappings
} from '../../../api/context/syncInventory';
import Loader from "../../../components/loader/loader";
import toast from 'react-hot-toast';
import { IoMdArrowBack } from 'react-icons/io';

export default function SyncInventory() {
    const { id: vendorId } = useParams();
    const [activeTab, setActiveTab] = useState('categories');
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Category tab states
    const [brands, setBrands] = useState([]);
    const [selectedBrandId, setSelectedBrandId] = useState(null);
    const [externalSubcategories, setExternalSubcategories] = useState([]);
    const [brandMappings, setBrandMappings] = useState({});
    const [allCategories, setAllCategories] = useState({});
    const [allSubcategories, setAllSubcategories] = useState({});
    const [savedCategoryMappings, setSavedCategoryMappings] = useState([]);

    // Size tab states
    const [activeSubcategory, setActiveSubcategory] = useState(null);
    const [sizeMappings, setSizeMappings] = useState({});
    const [internalSizes, setInternalSizes] = useState({});
    const [savedSizeMappings, setSavedSizeMappings] = useState([]);

    // Initialize empty mappings for categories
    const initializeCategoryMappings = (length) => {
        return Array(length).fill().map(() => ({
            category: null,
            subcategory: null
        }));
    };

    // Initialize empty mappings for sizes
    const initializeSizeMappings = (length) => {
        return Array(length).fill().map(() => ({
            internalSize: null
        }));
    };

    useEffect(() => {
        const fetchDataForActiveTab = async () => {
            try {
                setLoading(true);

                // Always fetch external subcategories as they're used in both tabs
                const subcategories = await fetchExternalSubcategories();

                if (activeTab === 'categories') {
                    // Fetch data needed for categories tab
                    const brandOptions = await fetchBrands();
                    setBrands(brandOptions);

                    if (brandOptions.length > 0) {
                        setSelectedBrandId(prev => prev ?? brandOptions[0].value);
                    }

                    const initialMappings = {};
                    const initialCategories = {};
                    const initialSubcategories = {};

                    await Promise.all(brandOptions.map(async brand => {
                        const hierarchy = await fetchBrandHierarchy(brand.value);
                        initialCategories[brand.value] = hierarchy;

                        const brandSubcategories = {};
                        hierarchy.forEach(category => {
                            brandSubcategories[category.id] = category.subcategories.map(sub => ({
                                value: sub.id,
                                label: sub.title
                            }));
                        });

                        initialSubcategories[brand.value] = brandSubcategories;
                        initialMappings[brand.value] = initializeCategoryMappings(subcategories.length);
                    }));

                    setAllCategories(initialCategories);
                    setAllSubcategories(initialSubcategories);
                    setBrandMappings(initialMappings);
                    await fetchSavedCategoryMappings(subcategories);
                } else {
                    // Fetch data needed for sizes tab
                    await fetchSavedSizeMappings();
                }

                setLoading(false);
            } catch (error) {
                console.error('Initialization error:', error);
                toast.error('Failed to load data');
                setLoading(false);
            }
        };

        fetchDataForActiveTab();
    }, [activeTab]); // Add activeTab as dependency

    const fetchSavedCategoryMappings = async (subcategories) => {
        return new Promise((resolve) => {
            getCategoryMappings({ vendorId })(response => {
                if (response?.statusCode === 200 && response.data) {
                    setSavedCategoryMappings(response.data);

                    // Create index map first
                    const externalIndexMap = {};
                    subcategories.forEach((sub, idx) => {
                        externalIndexMap[sub.id] = idx;
                    });

                    const newMappings = {};

                    response.data.forEach(mapping => {
                        const brandId = mapping.brand.id;
                        const idx = externalIndexMap[mapping.externalSubcategoryId];

                        if (idx !== undefined) {
                            if (!newMappings[brandId]) {
                                newMappings[brandId] = initializeCategoryMappings(subcategories.length);
                            }

                            newMappings[brandId][idx] = {
                                category: mapping.category ? {
                                    value: mapping.category.id,
                                    label: mapping.category.title
                                } : null,
                                subcategory: mapping.subcategory ? {
                                    value: mapping.subcategory.id,
                                    label: mapping.subcategory.title
                                } : null
                            };
                        }
                    });

                    // Merge carefully with existing state
                    setBrandMappings(prev => {
                        const merged = { ...prev };
                        Object.keys(newMappings).forEach(brandId => {
                            merged[brandId] = merged[brandId]
                                ? merged[brandId].map((item, i) => newMappings[brandId][i] || item)
                                : newMappings[brandId];
                        });
                        return merged;
                    });
                } else {
                    console.error('Failed to fetch saved category mappings or no data:', response);
                }
                resolve();
            });
        });
    };

    const fetchSavedSizeMappings = async () => {
        return new Promise((resolve) => {
            getSizeMappings({ vendorId })(response => {
                if (response?.statusCode === 200 && response.data) {
                    const apiData = response.data.data;
                    setSavedSizeMappings(apiData);

                    // Transform API data to our component structure
                    const externalSubcats = apiData.map(item => ({
                        id: item.externalSubcategoryId,
                        name: item.externalSubcategory,
                        category: item.externalCategory,
                        brandId: item.brandId,
                        internalMappings: item.internalMappings
                    }));

                    setExternalSubcategories(externalSubcats);

                    // Set the first subcategory as active by default if available
                    if (apiData.length > 0) {
                        setActiveSubcategory(apiData[0].externalSubcategoryId);
                    }

                    // Create internal sizes mapping
                    const internalSizesMap = {};
                    const mappings = {};

                    apiData.forEach(item => {
                        item.internalMappings.forEach(mapping => {
                            // Store internal sizes
                            internalSizesMap[mapping.internalSubcategoryId] =
                                mapping.internalSizes.map(size => ({
                                    value: size.id,
                                    label: size.size
                                }));

                            // Initialize mappings structure
                            if (!mappings[item.externalSubcategoryId]) {
                                mappings[item.externalSubcategoryId] = {};
                            }

                            // Create mappings for each external size
                            mappings[item.externalSubcategoryId][mapping.internalSubcategoryId] =
                                initializeSizeMappings(mapping.mappedSizes.length);

                            // Apply saved mappings
                            mapping.mappedSizes.forEach((mappedSize, idx) => {
                                if (mappedSize.internalSizeId) {
                                    mappings[item.externalSubcategoryId][mapping.internalSubcategoryId][idx] = {
                                        internalSize: {
                                            value: mappedSize.internalSizeId,
                                            label: mappedSize.internalSize
                                        }
                                    };
                                }
                            });
                        });
                    });

                    setInternalSizes(internalSizesMap);
                    setSizeMappings(mappings);
                } else {
                    console.error('Failed to fetch size mappings or no data:', response);
                }
                resolve();
            });
        });
    };

    // Fetch external subcategories
    const fetchExternalSubcategories = () => {
        return new Promise((resolve) => {
            getExternalSubcategoriesByVendor({ vendorId })(response => {
                if (response?.statusCode === 200) {
                    const subcategories = Array.isArray(response.data?.data)
                        ? response.data.data.map(item => ({
                            id: item.id,
                            name: item.externalSubcategory
                        }))
                        : [];
                    setExternalSubcategories(subcategories);
                    resolve(subcategories);
                } else {
                    console.error('Failed to fetch external subcategories:', response?.message);
                    toast.error('Failed to load external subcategories');
                    setExternalSubcategories([]);
                    resolve([]);
                }
            });
        });
    };

    // Fetch brand categories hierarchy
    const fetchBrandHierarchy = (brandId) => {
        return new Promise((resolve) => {
            getBrandCategoriesHierarchy({ brandId })(response => {
                if (response?.statusCode === 200) {
                    resolve(response.data || []);
                } else {
                    console.error('Failed to fetch brand hierarchy:', response?.message);
                    toast.error('Failed to load brand categories');
                    resolve([]);
                }
            });
        });
    };

    // Fetch brands
    const fetchBrands = (inputValue = '', page = 1, limit = 100) => {
        return new Promise((resolve) => {
            getBrandListing({  vendorId: vendorId,  page, pageSize: limit, search: inputValue })(response => {
                if (response?.statusCode === 200) {
                    const brandOptions = response.data.map(item => ({
                        value: item.id,
                        label: item.name,
                    }));
                    resolve(brandOptions);
                } else {
                    resolve([]);
                }
            });
        });
    };

    // Filter functions for AsyncSelect
    const filterCategories = (inputValue, brandId) => {
        if (!brandId) return [];
        const categories = allCategories[brandId] || [];
        const categoryOptions = categories.map(category => ({
            value: category.id,
            label: category.title
        }));
        return inputValue
            ? categoryOptions.filter(c => c.label.toLowerCase().includes(inputValue.toLowerCase()))
            : categoryOptions;
    };

    const filterSubcategories = (inputValue, brandId, categoryId) => {
        if (!brandId || !categoryId) return [];
        const subcategories = allSubcategories[brandId]?.[categoryId] || [];
        return inputValue
            ? subcategories.filter(s => s.label.toLowerCase().includes(inputValue.toLowerCase()))
            : subcategories;
    };

    // AsyncSelect loaders
    const loadOptionsCategories = (brandId, inputValue) => {
        return new Promise((resolve) => {
            setTimeout(() => resolve(filterCategories(inputValue, brandId)), 300);
        });
    };

    const loadOptionsSubcategories = (brandId, inputValue, categoryId) => {
        return new Promise((resolve) => {
            setTimeout(() => resolve(filterSubcategories(inputValue, brandId, categoryId)), 300);
        });
    };

    // Handlers for category tab
    const handleBrandSelect = (brandId) => {
        setSelectedBrandId(brandId);
    };

    const handleCategoryChange = (index, selectedOption) => {
        if (!selectedBrandId) return;

        setBrandMappings(prev => {
            const updated = { ...prev };
            updated[selectedBrandId] = [...updated[selectedBrandId]];
            updated[selectedBrandId][index] = {
                category: selectedOption,
                subcategory: null
            };
            return updated;
        });
    };

    const handleSubcategoryChange = (index, selectedOption) => {
        if (!selectedBrandId) return;

        setBrandMappings(prev => {
            const updated = { ...prev };
            updated[selectedBrandId] = [...updated[selectedBrandId]];
            updated[selectedBrandId][index].subcategory = selectedOption;
            return updated;
        });
    };

    // Handlers for size tab
    const handleSizeChange = (externalSubcatId, internalSubcatId, index, selectedOption) => {
        setSizeMappings(prev => {
            const updated = { ...prev };
            if (!updated[externalSubcatId]) {
                updated[externalSubcatId] = {};
            }
            if (!updated[externalSubcatId][internalSubcatId]) {
                updated[externalSubcatId][internalSubcatId] = initializeSizeMappings(0);
            }

            updated[externalSubcatId][internalSubcatId][index] = {
                internalSize: selectedOption
            };
            return updated;
        });
    };

    // Save handlers
    const handleSaveCategories = () => {
        setIsSubmitting(true);

        const hasMappings = Object.values(brandMappings).some(mappings =>
            mappings.some(m => m.category || m.subcategory)
        );

        if (!hasMappings) {
            toast.error('Please set at least one mapping');
            setIsSubmitting(false);
            return;
        }

        const payload = {
            vendorId,
            brandMappings: Object.entries(brandMappings).map(([brandId, mappings]) => ({
                brandId: parseInt(brandId),
                mappings: mappings.map((mapping, index) => ({
                    externalSubcategory: externalSubcategories[index].id,
                    categoryId: mapping.category?.value || null,
                    subcategoryId: mapping.subcategory?.value || null
                }))
            }))
        };

        saveCategoryMappings(payload)(response => {
            setIsSubmitting(false);
            if (response.statusCode === 200) {
                toast.success('Category mappings saved successfully!');
            } else {
                toast.error(response.message || 'Failed to save category mappings');
                console.error('Save error:', response.error);
            }
        });
    };

    const handleSaveSizes = () => {
        setIsSubmitting(true);

        const hasMappings = Object.values(sizeMappings).some(externalMappings =>
            Object.values(externalMappings).some(internalMappings =>
                internalMappings.some(m => m.internalSize)
            )
        );

        if (!hasMappings) {
            toast.error('Please set at least one mapping');
            setIsSubmitting(false);
            return;
        }

        const payload = {
            vendorId,
            sizeMappings: Object.entries(sizeMappings).flatMap(([externalSubcatId, internalMappings]) =>
                Object.entries(internalMappings).map(([internalSubcatId, mappings]) => ({
                    externalSubcategoryId: parseInt(externalSubcatId),
                    internalSubcategoryId: parseInt(internalSubcatId),
                    mappings: mappings.map((mapping, index) => ({
                        externalSize: savedSizeMappings
                            .find(sc => sc.externalSubcategoryId === parseInt(externalSubcatId))
                            ?.internalMappings.find(m => m.internalSubcategoryId === parseInt(internalSubcatId))
                            ?.mappedSizes[index]?.externalSize || '',
                        internalSizeId: mapping.internalSize?.value || null
                    }))
                })))
  };

        // Using your existing API call pattern
        saveSizeMappings(payload)((response) => {
            setIsSubmitting(false);

            if (response?.statusCode === 200) {
                toast.success('Size mappings saved successfully!');
                fetchSavedSizeMappings();
            } else {
                const errorMessage = response?.message || 'Failed to save size mappings';
                toast.error(errorMessage);
                console.error('Save error:', response?.error || errorMessage);
            }
        });
    };
    const customNoOptionsMessage = ({ inputValue }) => {
        return inputValue.length < 2 ? "Type 2+ characters" : "No options found";
    };



    const selectedBrand = brands.find(b => b.value === selectedBrandId);

    return (
        <div className="container-fluid d-flex flex-column text-dark pb-2 px-0">
            <div className="page-header-sticky d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
                <div className="d-flex align-items-start gap-3">
                    <Link
                        to={'/sync-inventory/' + vendorId}
                        className="text-dark"
                        style={{
                            cursor: 'pointer',
                            fontSize: '24px',
                            fontWeight: '800',
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 1,
                            marginTop: '4px',
                        }}
                    >
                        <IoMdArrowBack className='icon' />
                    </Link>
                    <div>
                        <h3 className="mb-1 fw-bold">Sync Inventory</h3>
                        <p className="text-muted mb-0">
                            Map your store categories and sizes to internal ones
                        </p>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn d-flex align-items-center gap-2"
                        onClick={() => setActiveTab('categories')}
                        style={{
                            borderRadius: '20px',
                            fontWeight: 600,
                            fontSize: '13px',
                            padding: '10px 16px',
                            backgroundColor: activeTab === 'categories' ? '#212529' : '#fff',
                            color: activeTab === 'categories' ? '#fff' : '#212529',
                            border: '1px solid #212529'
                        }}
                    >
                        <FiGrid size={16} />
                        Map Categories
                    </button>
                    <button
                        type="button"
                        className="btn d-flex align-items-center gap-2"
                        onClick={() => setActiveTab('sizes')}
                        style={{
                            borderRadius: '20px',
                            fontWeight: 600,
                            fontSize: '13px',
                            padding: '10px 16px',
                            backgroundColor: activeTab === 'sizes' ? '#212529' : '#fff',
                            color: activeTab === 'sizes' ? '#fff' : '#212529',
                            border: '1px solid #212529'
                        }}
                    >
                        <FiSliders size={16} />
                        Map Sizes
                    </button>
                </div>
            </div>

            {
                loading ?
                    <Loader />
                    : <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <Tab.Content>
                            <Tab.Pane eventKey="categories">
                                <div className="d-flex gap-4 px-4">
                                    {/* Brand list sidebar */}
                                    {brands.length > 1 && (
                                        <div className="d-flex flex-column" style={{ width: '220px' }}>
                                            <div className="card">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-2 fw-bold pt-2">Brands</h6>
                                                </div>
                                                <div className="card-body p-0">
                                                    <ul className="list-group list-group-flush">
                                                        {brands.map(brand => (
                                                            <li
                                                                key={brand.value}
                                                                className={`list-group-item list-group-item-action cursor-pointer ${selectedBrandId === brand.value ? 'active-brand' : ''}`}
                                                                onClick={() => handleBrandSelect(brand.value)}
                                                            >
                                                                {brand.label}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Main content */}
                                    <div className="flex-grow-1">
                                        {selectedBrandId ? (
                                            <div>
                                                <div className="card-header bg-light border border-1">
                                                    <h6 className="mb-2 fw-bold pt-2">Mapping for {selectedBrand?.label}</h6>
                                                </div>
                                                <div className="overflow-auto border rounded bg-white p-3">
                                                    <div className="row fw-bold mb-4 gx-2">
                                                        <div className="col-md-4">Brand Subcategory</div>
                                                        <div className="col-md-4">Getto's Category</div>
                                                        <div className="col-md-4">Getto's Subcategory</div>
                                                    </div>

                                                    {externalSubcategories.length > 0 ? (
                                                        externalSubcategories.map((external, index) => {
                                                            const mapping = brandMappings[selectedBrandId]?.[index] || { category: null, subcategory: null };
                                                            return (
                                                                <div className="row align-items-center mb-3 gx-2" key={index}>
                                                                    <div className="col-md-4">
                                                                        <div className="p-2 rounded bg-light">{external.name}</div>
                                                                    </div>
                                                                    <div className="col-md-4">
                                                                        <AsyncSelect
                                                                            loadOptions={(input) => loadOptionsCategories(selectedBrandId, input)}
                                                                            defaultOptions={filterCategories('', selectedBrandId)}
                                                                            value={mapping.category}
                                                                            onChange={(option) => handleCategoryChange(index, option)}
                                                                            classNamePrefix="select"
                                                                            noOptionsMessage={customNoOptionsMessage}
                                                                            placeholder=""
                                                                            styles={{
                                                                                control: (provided) => ({
                                                                                    ...provided,
                                                                                    minHeight: '38px',
                                                                                    border: "1px solid #a7a7a7",
                                                                                }),
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="col-md-4">
                                                                        <AsyncSelect
                                                                            loadOptions={(input) => loadOptionsSubcategories(selectedBrandId, input, mapping.category?.value)}
                                                                            defaultOptions={mapping.category?.value ? filterSubcategories('', selectedBrandId, mapping.category.value) : []}
                                                                            value={mapping.subcategory}
                                                                            onChange={(option) => handleSubcategoryChange(index, option)}
                                                                            isDisabled={!mapping.category}
                                                                            placeholder={""}
                                                                            classNamePrefix="select"
                                                                            noOptionsMessage={customNoOptionsMessage}
                                                                            styles={{
                                                                                control: (provided) => ({
                                                                                    ...provided,
                                                                                    minHeight: '38px',
                                                                                    border: "1px solid #a7a7a7",
                                                                                }),
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <div className="text-center py-4">No external subcategories found</div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '300px' }}>
                                                <div className="text-center">
                                                    <h5>No brand selected</h5>
                                                    <p>Please select a brand from the list</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="position-fixed bottom-0 end-0 p-3">
                                    <div className="d-flex justify-content-end">
                                        <button
                                            className="request_btn"
                                            onClick={handleSaveCategories}
                                            disabled={isSubmitting || brands.length === 0}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                    <span className="ms-2 text-white">Saving Category Mappings...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiSave className="me-2 text-white" />
                                                    <span className="text-white">Save Category Mappings</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Tab.Pane>

                            <Tab.Pane eventKey="sizes">
                                <div className="d-flex gap-4 px-4">
                                    {/* Subcategories sidebar */}
                                    <div className="d-flex flex-column" style={{ width: '260px' }}>
                                        <div className="card">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-2 fw-bold pt-2">Synced Subcategories</h6>
                                            </div>
                                            <div className="card-body p-0">
                                                <ul className="list-group list-group-flush">
                                                    {externalSubcategories.map(subcategory => (
                                                        <li
                                                            key={subcategory.id}
                                                            className={`list-group-item list-group-item-action cursor-pointer ${activeSubcategory === subcategory.id ? 'active-brand' : ''}`}
                                                            onClick={() => setActiveSubcategory(subcategory.id)}
                                                        >
                                                            <div className="d-flex flex-column">
                                                                <span>{subcategory.name}</span>
                                                                {subcategory.internalMappings?.length > 0 && (
                                                                    <small className="">
                                                                        {subcategory.internalMappings.map(m => m.internalSubcategoryTitle).join(', ')}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Main content */}
                                    <div className="flex-grow-1">
                                        {externalSubcategories.length > 0 ? (
                                            <div>
                                                {externalSubcategories
                                                    .filter(subcat => subcat.id === activeSubcategory)
                                                    .map(externalSubcat => (
                                                        <div key={externalSubcat.id}>
                                                            <div className="card-header bg-light border border-1">
                                                                <h6 className="mb-2 fw-bold pt-2">Size Mapping for {externalSubcat.name}</h6>
                                                            </div>
                                                            <div className="overflow-auto border rounded bg-white p-3 mb-4">
                                                                {(() => {
                                                                    const mappingsForSubcat = savedSizeMappings.find(sc => sc.externalSubcategoryId === externalSubcat.id);
                                                                    if (!mappingsForSubcat || !mappingsForSubcat.internalMappings || mappingsForSubcat.internalMappings.length === 0) {
                                                                        return (
                                                                            <div
                                                                                className="d-flex flex-column align-items-center justify-content-center text-center px-3"
                                                                                style={{ minHeight: '60vh' }}
                                                                            >
                                                                                <FiShuffle size={40} className="text-secondary mb-3" />
                                                                                <h6 className="fw-bold mb-2">No category mapped yet</h6>
                                                                                <p className="text-muted mb-3" style={{ maxWidth: 420 }}>
                                                                                    Map <strong>{externalSubcat.name}</strong> to a Getto category first — size mapping options will appear here once that&apos;s done.
                                                                                </p>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn d-inline-flex align-items-center gap-2"
                                                                                    onClick={() => setActiveTab('categories')}
                                                                                    style={{
                                                                                        borderRadius: '20px',
                                                                                        fontWeight: 600,
                                                                                        fontSize: '13px',
                                                                                        padding: '10px 16px',
                                                                                        backgroundColor: '#212529',
                                                                                        color: '#fff',
                                                                                        border: '1px solid #212529'
                                                                                    }}
                                                                                >
                                                                                    <FiGrid size={16} />
                                                                                    Go to Map Categories
                                                                                </button>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    return mappingsForSubcat.internalMappings.map(internalMapping => (
                                                                        <div key={internalMapping.internalSubcategoryId}>
                                                                            <div className="row fw-bold mb-3 gx-2">
                                                                                <div className="col-md-6">Brand Size</div>
                                                                                <div className="col-md-6">Getto standard Size</div>
                                                                            </div>

                                                                            {internalMapping.mappedSizes.map((mappedSize, index) => {
                                                                                const currentMapping = sizeMappings[externalSubcat.id]?.[internalMapping.internalSubcategoryId]?.[index] || { internalSize: null };
                                                                                return (
                                                                                    <div className="row align-items-center mb-3 gx-2" key={index}>
                                                                                        <div className="col-md-6">
                                                                                            <div className="p-2 rounded bg-light">{mappedSize.externalSize}</div>
                                                                                        </div>
                                                                                        <div className="col-md-6">
                                                                                            <AsyncSelect
                                                                                                loadOptions={(input) =>
                                                                                                    new Promise(resolve => {
                                                                                                        const options = internalSizes[internalMapping.internalSubcategoryId] || [];
                                                                                                        resolve(
                                                                                                            input
                                                                                                                ? options.filter(opt =>
                                                                                                                    opt.label.toLowerCase().includes(input.toLowerCase()))
                                                                                                                : options
                                                                                                        );
                                                                                                    })
                                                                                                }
                                                                                                defaultOptions={internalSizes[internalMapping.internalSubcategoryId] || []}
                                                                                                value={currentMapping.internalSize}
                                                                                                onChange={(option) =>
                                                                                                    handleSizeChange(
                                                                                                        externalSubcat.id,
                                                                                                        internalMapping.internalSubcategoryId,
                                                                                                        index,
                                                                                                        option
                                                                                                    )
                                                                                                }
                                                                                                classNamePrefix="select"
                                                                                                noOptionsMessage={customNoOptionsMessage}
                                                                                                placeholder=""
                                                                                                styles={{
                                                                                                    control: (provided) => ({
                                                                                                        ...provided,
                                                                                                        minHeight: '38px',
                                                                                                        border: "1px solid #a7a7a7",
                                                                                                    }),
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ));
                                                                })()}
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        ) : (
                                            <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '300px' }}>
                                                <div className="text-center">
                                                    <h5>No subcategories found</h5>
                                                    <p>Please check your data</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="position-fixed bottom-0 end-0 p-3">
                                    <div className="d-flex justify-content-end">
                                        <button
                                            className="request_btn"
                                            onClick={handleSaveSizes}
                                            disabled={isSubmitting || externalSubcategories.length === 0}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                    <span className="ms-2 text-white">Saving Size Mappings...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiSave className="me-2 text-white" />
                                                    <span className="text-white">Save Size Mappings</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
            }


        </div>
    );
}